import { UserSubscriptionPlan } from "../../constants/GlobalTypesAndEnumsAndConstants";

// Plan-aware readers of a pricing-details object (see upsellPricingHelpers.ts, which re-exports them for
// the app). ELITE reads the Elite fields; everything else reads the Super Learner fields (Pro and the
// fallback). Pure functions: the marketing site's pricing page uses them without the app's runtime.

export function getMonthlyPriceAmountForPlan(
  subscriptionPricingDetails: any,
  subscriptionPlan: UserSubscriptionPlan
): string | undefined {
  if (subscriptionPlan === UserSubscriptionPlan.ELITE) {
    return subscriptionPricingDetails?.formattedEliteMonthlyPrice;
  }
  return subscriptionPricingDetails?.formattedSuperLearnerMonthlyPrice;
}

export function getAnnualProratedMonthlyPriceAmountForPlan(
  subscriptionPricingDetails: any,
  subscriptionPlan: UserSubscriptionPlan
): string | undefined {
  if (subscriptionPlan === UserSubscriptionPlan.ELITE) {
    return subscriptionPricingDetails?.formattedEliteAnnualPriceProratedMonthly;
  }
  return subscriptionPricingDetails?.formattedSuperLearnerAnnualPriceProratedMonthly;
}

export function getFullYearMonthlyPriceForPlan(
  subscriptionPricingDetails: any,
  subscriptionPlan: UserSubscriptionPlan
): string | undefined {
  if (subscriptionPlan === UserSubscriptionPlan.ELITE) {
    return subscriptionPricingDetails?.formattedElitePriceOfMonthlyIfPaidForFullYear;
  }
  return subscriptionPricingDetails?.formattedSuperLearnerPriceOfMonthlyIfPaidForFullYear;
}

export function getFullAnnualPriceForPlan(
  subscriptionPricingDetails: any,
  subscriptionPlan: UserSubscriptionPlan
): string | undefined {
  if (subscriptionPlan === UserSubscriptionPlan.ELITE) {
    return subscriptionPricingDetails?.formattedEliteFullAnnualPrice;
  }
  return subscriptionPricingDetails?.formattedSuperLearnerFullAnnualPrice;
}

/**
 * Reads a displayed price amount back as a number: "37", "1,234.5", "1.234,5", "12 345,5", "14,99",
 * or the ar-EG / ar-SA formatter's "١٢٬٣٤٥٫٥". Store prices are localized strings too. The formatters
 * emit at most two fraction digits, so a final separator followed by three digits groups thousands.
 * Returns NaN when the string holds no number.
 */
export function parseDisplayedPriceAmount(displayedAmount: string | null | undefined): number {
  if (!displayedAmount) return NaN;
  const westernDigitsAmount = displayedAmount
    .replace(/[\u0660-\u0669]/g, (arabicIndicDigit) => String(arabicIndicDigit.charCodeAt(0) - 0x0660))
    .replace(/[\u06f0-\u06f9]/g, (extendedArabicIndicDigit) => String(extendedArabicIndicDigit.charCodeAt(0) - 0x06f0))
    .replace(/\u066b/g, ".") // Arabic decimal separator
    .replace(/\u066c/g, ","); // Arabic thousands separator

  const digitsAndSeparators = westernDigitsAmount.replace(/[^\d.,]/g, "");
  if (!/\d/.test(digitsAndSeparators)) return NaN;
  const lastSeparatorIndex = Math.max(digitsAndSeparators.lastIndexOf("."), digitsAndSeparators.lastIndexOf(","));
  const digitCountAfterLastSeparator = lastSeparatorIndex < 0 ? 0 : digitsAndSeparators.length - lastSeparatorIndex - 1;
  const isLastSeparatorDecimal = digitCountAfterLastSeparator >= 1 && digitCountAfterLastSeparator <= 2;
  const integerDigits = (
    isLastSeparatorDecimal ? digitsAndSeparators.slice(0, lastSeparatorIndex) : digitsAndSeparators
  ).replace(/[.,]/g, "");
  const fractionDigits = isLastSeparatorDecimal ? digitsAndSeparators.slice(lastSeparatorIndex + 1) : "0";
  return Number(`${integerDigits || "0"}.${fractionDigits}`);
}

/**
 * The monthly plan's price to show struck through beside the yearly plan's per-month price
 * ("$15 ~~$37~~ per month"): the same amount the monthly toggle shows, from the pricing source only
 * (never a design default). Undefined when either price is missing, or when the monthly price is not
 * higher, since then there is no saving to show.
 */
export function getMonthlyPriceAmountToStrikeThroughBesideYearlyPrice(
  subscriptionPricingDetails: any,
  subscriptionPlan: UserSubscriptionPlan
): string | undefined {
  const monthlyPriceAmount = getMonthlyPriceAmountForPlan(subscriptionPricingDetails, subscriptionPlan);
  const yearlyPerMonthPriceAmount = getAnnualProratedMonthlyPriceAmountForPlan(
    subscriptionPricingDetails,
    subscriptionPlan
  );
  const monthlyPrice = parseDisplayedPriceAmount(monthlyPriceAmount);
  const yearlyPerMonthPrice = parseDisplayedPriceAmount(yearlyPerMonthPriceAmount);
  // NaN fails every comparison, so an unreadable amount on either side renders nothing.
  if (!(yearlyPerMonthPrice > 0 && monthlyPrice > yearlyPerMonthPrice)) return undefined;
  return monthlyPriceAmount ?? undefined;
}

/**
 * The yearly saving every paywall and the pricing page show while prices are loading, or when the pricing
 * source has no usable numbers for the plans on screen (US prices: Pro $15/mo vs $72/yr is 60%).
 */
export const ANNUAL_DISCOUNT_PERCENTAGE_FALLBACK = 60;

/**
 * The yearly-saving percentage to display for a computed one. The nearest multiple of 5 reads cleanly, but it
 * is only used when it overstates the real saving by at most 1 point; otherwise the computed percent rounded
 * down is shown. 60.0 → 60, 59.46 → 60, 62.4 → 60, 63.1 → 63 (65 would overstate by 1.9).
 */
export function roundAnnualDiscountPercentageForDisplay(computedDiscountPercentage: number): number {
  // Drop floating-point noise first, so an exact 59 computed as 58.9999999 is not floored to 58.
  const discountPercentage = Math.round(computedDiscountPercentage * 1e6) / 1e6;
  const nearestMultipleOfFive = Math.round(discountPercentage / 5) * 5;
  if (nearestMultipleOfFive - discountPercentage <= 1) return nearestMultipleOfFive;
  return Math.floor(discountPercentage);
}

/**
 * The unrounded yearly saving of one plan from RAW numeric prices, or null when the pricing source has no
 * usable numbers for it (not loaded yet, a plan the source does not sell, or a yearly price that is not
 * cheaper than twelve months).
 */
function calculateUnroundedAnnualDiscountPercentageFromRawPrices(
  priceDetails: any,
  subscriptionPlan: UserSubscriptionPlan
): number | null {
  const isElite = subscriptionPlan === UserSubscriptionPlan.ELITE;
  const monthlyPrice = Number(isElite ? priceDetails?.monthlyElitePrice : priceDetails?.monthlySuperLearnerPrice);
  const annualPrice = Number(isElite ? priceDetails?.annualElitePrice : priceDetails?.annualSuperLearnerPrice);
  const fullYearPriceIfPaidMonthly = monthlyPrice * 12;
  if (!(monthlyPrice > 0 && annualPrice > 0 && fullYearPriceIfPaidMonthly > annualPrice)) return null;
  return ((fullYearPriceIfPaidMonthly - annualPrice) / fullYearPriceIfPaidMonthly) * 100;
}

/**
 * The yearly-saving percentage to display, from the RAW numeric prices on `priceDetails` (the inner object
 * returned by the pricing fetchers; formatted strings like "1,200" would parse as 1). This is the one place
 * the displayed saving is decided, so the paywall note, the savings badge and the pricing page agree.
 *
 * Pass every plan whose card the note describes: one note over several cards shows the smallest saving, so it
 * never overstates any of them. Plans without usable prices are skipped; with none, the fallback is shown.
 */
export function calculateAnnualDiscountPercentageFromRawPrices(
  priceDetails: any,
  subscriptionPlans: UserSubscriptionPlan | UserSubscriptionPlan[]
): number {
  const plansOnScreen = Array.isArray(subscriptionPlans) ? subscriptionPlans : [subscriptionPlans];
  const computedDiscountPercentages = plansOnScreen
    .map((subscriptionPlan) => calculateUnroundedAnnualDiscountPercentageFromRawPrices(priceDetails, subscriptionPlan))
    .filter((discountPercentage): discountPercentage is number => discountPercentage !== null);
  if (computedDiscountPercentages.length === 0) return ANNUAL_DISCOUNT_PERCENTAGE_FALLBACK;
  return roundAnnualDiscountPercentageForDisplay(Math.min(...computedDiscountPercentages));
}
